import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Bot, Syringe, FileSignature, ShieldCheck, ShieldAlert, Loader2,
  CheckCircle2, ChevronRight, Sparkles, Clock, Send, Zap, TrendingUp,
  PlugZap, Unplug, MessageCircleHeart, Plug,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Catalog of automation agents. New agents are added here as they ship.
const AGENTS = [
  {
    agent_type: "vaccine_compliance",
    name: "Vaccine Compliance Agent",
    tagline: "Auto-sends Gingr vaccine reminders",
    description: "Scans the Expired Vaccinations report and sends Gingr-native Email Reminders to customers with upcoming reservations and missing or expired vaccine records.",
    Icon: Syringe,
    accent: "from-cyan-500 to-blue-600",
    accentBg: "bg-cyan-50",
    accentText: "text-cyan-600",
    path: "/agents/vaccine-compliance",
  },
  {
    agent_type: "unsigned_agreements",
    name: "Unsigned Agreements Agent",
    tagline: "Texts the agreement signature link",
    description: "Scans the Unsigned Agreements report, pushes the agreement to the customer's device and sends the signature-capture SMS — all natively inside Gingr.",
    Icon: FileSignature,
    accent: "from-violet-500 to-fuchsia-600",
    accentBg: "bg-violet-50",
    accentText: "text-violet-600",
    path: "/agents/unsigned-agreements",
  },
  {
    agent_type: "churn_nudge",
    name: "Churn Nudge Agent",
    tagline: "Wins back lapsed daycare regulars",
    description: "Texts a friendly come-back nudge to daycare regulars who stopped coming — you curate the queue, it sends via Gingr at noon on weekdays. Runs safely on a test list first.",
    Icon: MessageCircleHeart,
    accent: "from-rose-500 to-pink-600",
    accentBg: "bg-rose-50",
    accentText: "text-rose-500",
    path: "/agents/churn-nudge",
    overviewUrl: "/ops/churn-nudge/agent-overview",
  },
];

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

export default function Agents() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statusMap, setStatusMap] = useState({});
  const [connection, setConnection] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/compliance-agent/agents-overview`);
      const map = {};
      (data.agents || []).forEach((a) => { map[a.agent_type] = a; });
      // Churn Nudge lives in its own service — fetch + normalize into the same shape.
      try {
        const { data: cn } = await axios.get(`${API}/ops/churn-nudge/agent-overview`);
        const lr = cn.last_run;
        map["churn_nudge"] = {
          agent_type: "churn_nudge",
          configured: cn.configured,
          live_enabled: cn.live_enabled,
          paused: false,
          schedule_enabled: cn.schedule_enabled,
          stats: cn.stats,
          last_run: lr ? { created_at: lr.ran_at, status: lr.status, summary: { sent: lr.sent || 0 } } : null,
        };
      } catch { /* churn overview optional */ }
      setStatusMap(map);
      setConnection(data.connection || { connected: !!data.has_connection });
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = Object.values(statusMap).reduce(
    (acc, a) => {
      acc.sentWeek += a.stats?.sent_this_week || 0;
      acc.runsWeek += a.stats?.runs_this_week || 0;
      acc.sentTotal += a.stats?.sent_total || 0;
      return acc;
    },
    { sentWeek: 0, runsWeek: 0, sentTotal: 0 }
  );

  return (
    <div className="space-y-6" data-testid="agents-hub-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Bot className="h-7 w-7 text-cyan-600" />
            Agents
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Your always-on Gingr automation agents. Each one runs safely in dry-run first, then live sending when you arm it.
          </p>
        </div>
        {!loading && connection && (
          connection.connected ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 h-fit px-3 py-1.5" data-testid="gingr-connection-chip" title={connection.email_masked || undefined}>
              <PlugZap className="h-3.5 w-3.5 mr-1.5" />
              Gingr login connected{connection.email_masked ? ` · ${connection.email_masked}` : ""}
            </Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 border-slate-200 h-fit px-3 py-1.5" data-testid="gingr-connection-chip">
              <Unplug className="h-3.5 w-3.5 mr-1.5" />
              No Gingr login connected
            </Badge>
          )
        )}
      </div>

      {/* ROI rollup banner */}
      {!loading && (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-cyan-50 via-white to-violet-50 px-5 py-4" data-testid="roi-banner">
          <div className="grid grid-cols-3 divide-x divide-gray-200">
            <div className="px-2 sm:px-4">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <Send className="h-3.5 w-3.5 text-cyan-600" /> Sent this week
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1" data-testid="roi-sent-week">{totals.sentWeek}</div>
            </div>
            <div className="px-2 sm:px-4">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <Zap className="h-3.5 w-3.5 text-violet-600" /> Runs this week
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1" data-testid="roi-runs-week">{totals.runsWeek}</div>
            </div>
            <div className="px-2 sm:px-4">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> Sent all-time
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1" data-testid="roi-sent-total">{totals.sentTotal}</div>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">Automated Gingr actions sent by your agents — hours of manual follow-up, handled for you.</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-cyan-600" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {AGENTS.map((a) => {
            const st = statusMap[a.agent_type] || {};
            const AgentIcon = a.Icon;
            const last = st.last_run;
            return (
              <Card
                key={a.agent_type}
                onClick={() => navigate(a.path)}
                className="group cursor-pointer border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-200"
                data-testid={`agent-card-${a.agent_type}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className={`h-12 w-12 rounded-xl ${a.accentBg} flex items-center justify-center shrink-0`}>
                        <AgentIcon className={`h-6 w-6 ${a.accentText}`} />
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-lg font-bold text-gray-900 truncate">{a.name}</h2>
                        <p className="text-xs font-medium text-gray-500">{a.tagline}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-cyan-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </div>

                  <p className="text-sm text-gray-600 mt-4 leading-relaxed">{a.description}</p>

                  <div className="flex items-center gap-2 mt-4 flex-wrap">
                    {!st.configured ? (
                      <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid={`status-${a.agent_type}`}>
                        Not configured
                      </Badge>
                    ) : st.live_enabled ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`status-${a.agent_type}`}>
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" />Live armed
                      </Badge>
                    ) : (
                      <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200" data-testid={`status-${a.agent_type}`}>
                        <ShieldAlert className="h-3.5 w-3.5 mr-1" />Approval mode
                      </Badge>
                    )}
                    {st.paused && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">Paused</Badge>
                    )}
                    {st.schedule_enabled && (
                      <Badge variant="outline" className="border-gray-200 text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />Daily auto-run
                      </Badge>
                    )}
                  </div>

                  {st.configured && st.stats && (
                    <div className="grid grid-cols-3 gap-2 mt-4" data-testid={`stats-${a.agent_type}`}>
                      {[
                        ["Sent / wk", st.stats.sent_this_week],
                        ["Runs / wk", st.stats.runs_this_week],
                        ["Sent total", st.stats.sent_total],
                      ].map(([k, v]) => (
                        <div key={k} className="rounded-lg bg-gray-50 border border-gray-100 py-2 text-center">
                          <div className="text-lg font-bold text-gray-900">{v}</div>
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">{k}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {last && (
                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <span>
                        Last run {fmt(last.created_at)} · <b className="text-emerald-600">{last.summary?.sent ?? 0}</b> sent
                        {" · "}<span className="capitalize">{last.status}</span>
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Smart Lead Follow-Up — ranks open leads, drafts rep-approved texts (Phase-1 preview) */}
          <Card
            onClick={() => navigate("/agents/smart-lead-followup")}
            className="group cursor-pointer border-gray-200 hover:border-amber-300 hover:shadow-lg transition-all duration-200"
            data-testid="agent-card-smart-lead-followup"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                    <Sparkles className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 truncate">Smart Lead Follow-Up</h2>
                    <p className="text-xs font-medium text-gray-500">Rank leads · draft rep-approved texts</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>
              <p className="text-sm text-gray-600 mt-4 leading-relaxed">
                Tells reps who to contact now and why, drafts a warm SMS, and sends through Gingr once a rep approves. A person always reviews — no auto-send.
              </p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="status-smart-lead-followup">
                  <Sparkles className="h-3.5 w-3.5 mr-1" />Preview · mockup
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Agent Setup — the shared Gingr login every agent uses for this location */}
          <Card
            onClick={() => navigate("/agent-setup")}
            className="group cursor-pointer border-gray-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-200"
            data-testid="agent-card-agent-setup"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                    <Plug className="h-6 w-6 text-teal-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 truncate">Agent Setup</h2>
                    <p className="text-xs font-medium text-gray-500">Connect this location's Gingr login</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-cyan-500 group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>
              <p className="text-sm text-gray-600 mt-4 leading-relaxed">
                Enter the Gingr automation login every agent shares for this location, then Test Login to confirm it works. A failure is logged to the Command Center for troubleshooting.
              </p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {connection?.connected ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="status-agent-setup" title={connection.email_masked || undefined}>
                    <PlugZap className="h-3.5 w-3.5 mr-1" />Connected{connection.email_masked ? ` · ${connection.email_masked}` : ""}
                  </Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid="status-agent-setup">
                    <Unplug className="h-3.5 w-3.5 mr-1" />Not connected
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Coming soon teaser */}
          <Card className="border-dashed border-2 border-gray-200 bg-gray-50/40" data-testid="agent-card-coming-soon">
            <CardContent className="p-6 flex items-center gap-4 h-full">
              <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                <Sparkles className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-500">More agents coming soon</h2>
                <p className="text-sm text-gray-400 mt-0.5">Deposit reminders, review requests, waitlist fills & more.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
